import React from 'react'
import connectionFailure from './sagas/connectionFailure'
import Store from './store'
import * as authActions from 'actions/authActions'
import * as modalActions from 'actions/modalActions'
import debug from 'Helper/debugHelpers'
import globalConfig from 'Helper/configHelpers'
import { put, select } from 'redux-saga/effects'
import { delay } from 'Helper/customReduxEffects.js'
import ValidationModal from 'GeneralUI/ValidationModal/ValidationModal.jsx'
import StandardModalContent from 'GeneralUI/StandardModalContent/StandardModalContent.jsx'

// this is an adapted version of Helper/routeHelpers push that has limited features
// so that it doesn't import the store. Otherwise the app loads in an order that
// prevents redux dev tools from displaying properly.
const push = route => {
  if (route.indexOf(globalConfig.basepath) !== 0) {
    route = (globalConfig.basepath + route).replace('//', '/')
  }
  globalConfig.navigate(route)
}

export const apiCallbacks = {
  200: defaultSuccessCallback,
  201: defaultSuccessCallback,
  202: defaultSuccessCallback,
  204: defaultSuccessCallback,
  400: defaultValidationCallback,
  401: default401Callback,
  419: default401Callback, // bad csrf token
  403: default403Callback,
  404: default404Callback,
  422: defaultValidationCallback,
  429: default429Callback,
  500: default500Callback,
  503: default503Callback,
  504: default504Callback,
}

export const errorHandlers = {
  offlineErrorHandler,
  generalErrorHandler,
  maxCountErrorHandler,
  defaultDownloadErrorHandler,
  singleErrorHandler: function* () {
    return yield null
  }
}

export function* defaultDownloadErrorHandler(filename) {
  yield put(
    modalActions.openModal('file_download_error_modal', {
      title: 'File download error.',
      closeable: true,
      content:
        'An error occurred. The file ' + filename + ' could not be downloaded.'
    })
  )
}

export function* maxCountErrorHandler() {
  // if there have been at least 3 consecutive failures show the failure modal
  const message = (
    <div>
      Summit is experiencing connectivity issues. Verify your internet
      connection and click ok to login again.
      <br />
      <span style={{ fontSize: '.8em' }}>API failure count exceeded.</span>
    </div>
  )
  yield put(
    modalActions.openModal('connection_problems', {
      title: 'Internet connection problem.',
      content: (
        <StandardModalContent
          messageContent={message}
          okHandler={() => {
            // this context is the xtractAPI object which has store set
            debug.log(
              'doing logout after click on connection problem modal.',
              debug.logLevels.AUTH
            )
            Store.dispatch(authActions.doLogout())
          }}
          okText={'OK'}
        />
      ),
      closeable: true
    })
  )
}

export function* generalErrorHandler(count, error) {
  // dispatch the update modal action which ultimately results in a modal being displayed warning the user about the internet dropping out

  const message = (
    <div>
      Summit is experiencing connectivity issues. Verify your internet
      connection and try reloading the application if necessary.
      <br />
      {error.name + ': ' + error.message}
      <br />
    </div>
  )
  yield put(
    modalActions.openModal('connection_problems', {
      title: 'Internet connection problem.',
      content: message,
      closeable: true
    })
  )
}

export function* offlineErrorHandler(count, error) {
  const message = <div>Your browser has lost connection to the internet.</div>
  yield put(
    modalActions.openModal('connection_problems', {
      title: 'Internet connection problem.',
      content: message,
      closeable: true
    })
  )
}

// default success callback for all 2xx status codes (200, 201, 202, 204)
// do nothing - a redux action was already submitted that will store the results if a reducer is setup to listen
export function defaultSuccessCallback(response, action) {
  return null
}

// default 403 callback. We only return 403 when email needs to be verified so push to
// the email verification route.
export function default403Callback(response, action) {
  if (response.data.missing_email) {
    push('login?missing_email=true')
  } else if (response.data.unverified_email) {
    push('login?unverified_email=true')
  }
  return null
}

// default validation callback for validation error responses (400, 422)
// shows a popup listing all validation errors
export function* defaultValidationCallback(response, action) {
  const validationData = response.data
  
  // alert with the message
  yield put(
    modalActions.openModal('validation_modal', {
      title: 'Validation error',
      content: (
        <ValidationModal
          data={validationData}
          okHandler={
            // on ok, click, merge any overrides returned into the original object and re-dispatch
            dataToAdd => {
              const data = action.obj || {}
              const newData = { ...data, ...dataToAdd }
              action.obj = newData
              // this context is the xtractAPI object which has store set
              Store.dispatch(action)
            }
          }
        />
      ),
      closeable: true
    })
  )
  return null
}

// default api callback if a status code 401 (not authorized) is returned. Most likely
// this is due to a stale auth token so do a token refresh and then retry the same api
// call
export function* default401Callback(response, action) {
  if (
    response.data &&
    response.data.message &&
    response.data.message.match(/^privilege_error:/)
  ) {
    yield put(
      modalActions.openModal('user_privilege_modal', {
        title: 'Insufficient privilege to perform action',
        content: (
          <StandardModalContent
            messageContent={response.data.message}
            okHandler={() => {
              Store.dispatch(modalActions.closeModal('user_privilege_modal'))
            }}
            okText={'OK'}
          />
        ),
        closeable: true
      })
    )
  } else if (
    response.data &&
    typeof response.data === 'string' &&
    response.data.toLowerCase() === 'unauthenticated.'
  ) {
    const auth = yield select(state => state.auth)
    if (yield* connectionFailure(auth.API_FAIL_COUNT)) {
      // token refresh
      yield put(authActions.doTokenRefresh(action))
    }
  }
  return null
}

// default callback for api calls resulting in 404 (not found) status code. Assuming
// the developer gave it a URL that really does exist, a 404 would likely be a
// momentary glitch with internet trafic so just retry the call but use
// connectionFailure which does thing like keep track of the number of retries so we
// wont retry for forever (only 3 times before we give up)
export function* default404Callback(response, action) {
  debug.logObject(
    'An api request was made resulting in 404 not found',
    action,
    debug.logLevels.API,
    'error'
  )

  // let the user know something funky happened
  yield put(
    modalActions.openModal('not_found_modal', {
      title: 'Not found',
      content: (
        <StandardModalContent
          messageContent={
            <div>
              {
                'The application attempted to interact with a resource that could not be found.'
              }
              <div>{'resource: ' + action.resource}</div>
              <div>{'type: ' + action.method}</div>
            </div>
          }
          okHandler={() => {
            Store.dispatch(modalActions.closeModal('not_found_modal'))
          }}
          okText={'OK'}
        />
      ),
      closeable: true
    })
  )
  return null
}

// default callback for status code 429 (api throttling). In this case, we are making
// to many api calls to fast so if we get this, wait 10 seconds for throttling to reset
// and then try the same api call again
export function* default429Callback(response, action) {
  yield delay(10000)
  yield put({ ...action })
  debug.log(
    'doing retry of the same call after 429 (throttle).',
    debug.logLevels.API,
    'warn'
  )
}

// default callback for api calls resulting in 500 status code (server error)
// not much we can do here so just do nothing.
export function* default500Callback(response, action) {
  const { data = {} } = response
  const { message = {} } = data
  const { message: messageString = '' } = message
  if (messageString.toLowerCase() === 'server error') {
    const { auth } = yield select()
    if (yield* connectionFailure(auth.API_FAIL_COUNT)) {
      debug.log('Server error 500, doing retry.', debug.logLevels.API, 'warn')
      // schedule a retry of the same API call in 1 second
      yield delay(1000)
      // reschedule the call to try again after refresh completes
      yield put({ ...action })
    }
  }
  return null
}

// default callback for api calls resulting in 503 status code (Service Unavailable)
// Server temporarily unavailable, retry with backoff
export function* default503Callback(response, action) {
  const { auth } = yield select()
  if (yield* connectionFailure(auth.API_FAIL_COUNT)) {
    debug.log('Service unavailable (503), doing retry.', debug.logLevels.API, 'warn')
    // schedule a retry of the same API call in 1 second
    yield delay(1000)
    // reschedule the call to try again
    yield put({ ...action })
  }
  return null
}

// default callback for api calls resulting in 504 status code (Gateway Timeout)
// Gateway timeout, retry with backoff
export function* default504Callback(response, action) {
  const { auth } = yield select()
  if (yield* connectionFailure(auth.API_FAIL_COUNT)) {
    debug.log('Gateway timeout (504), doing retry.', debug.logLevels.API, 'warn')
    // schedule a retry of the same API call in 1 second
    yield delay(1000)
    // reschedule the call to try again
    yield put({ ...action })
  }
  return null
}
